import { useAuth } from '@clerk/clerk-react';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { useSpaceScope } from '@/contexts/SpaceScopeContext';
import { spacesApi, transactionsApi } from '@/lib/api';
import { notifySpaceSendingChanged } from '@/lib/spaces/sendingChanges';
import type { MySpaceSendRules, SendToSpaceRequest, SpaceBatchPreview } from '@/types/spaces';
import { LONG_LIVED_SWR_OPTIONS, revalidateTransactionCaches, useAuthedFetcher } from './swrConfig';
import { useSpaceWrite } from './useSpaceWrite';

const OPTIONS = { ...LONG_LIVED_SWR_OPTIONS, keepPreviousData: false };

export function useSendingTransactionLookup() {
  const { userId } = useAuth();
  const { enabled, scope } = useSpaceScope();
  const fetch = useAuthedFetcher();
  return useSWRMutation(['space-send-source', userId], async (_key, { arg }: { arg: string }) => {
    if (!enabled || scope !== 'personal' || !userId) throw new Error('O envio não está disponível.');
    return fetch((token) => transactionsApi.getOne(token, arg));
  });
}

export function useMySpaceSendRules(active: boolean) {
  const { userId } = useAuth();
  const { enabled, scope } = useSpaceScope();
  const fetch = useAuthedFetcher();
  return useSWR<MySpaceSendRules, Error>(active && enabled && scope === 'personal' && userId ? ['space-send-rules', userId] : null,
    () => fetch(spacesApi.sendRules), OPTIONS);
}

export function useSpaceBatchPreview(spaceId: string | null, transactionId: string) {
  const { userId } = useAuth();
  const { enabled, scope } = useSpaceScope();
  const fetch = useAuthedFetcher();
  return useSWR<SpaceBatchPreview, Error>(enabled && scope === 'personal' && userId && spaceId ? ['space-send-preview', userId, spaceId, transactionId] : null,
    () => fetch((token) => spacesApi.previewSend(token, spaceId!, transactionId)), { ...OPTIONS, shouldRetryOnError: false });
}

export function useSendSpaceExpense(spaceId: string) {
  const { write, userId, mutate, refresh } = useSpaceWrite();
  return useSWRMutation(['space-send', userId, spaceId], async (_key, { arg }: { arg: SendToSpaceRequest }) => {
    const result = await write((token) => spacesApi.send(token, spaceId, arg));
    // The rows carry the spaces they are in (VER-571): every mounted transaction surface reads its markers again, and
    // with them every read that says what is in the person's spaces.
    revalidateTransactionCaches(mutate);
    notifySpaceSendingChanged();
    refresh(['space-send-rules', userId]);
    return result;
  });
}
